import React, { useState, useEffect } from 'react';
import apiClient from '../../assets/scripts/services/apiClient';

const AccessRulesModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  page, 
  applicationId, 
  organizationId 
}) => {
  const [userTypes, setUserTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserTypes, setSelectedUserTypes] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Only load user types when modal opens
      loadUserTypes();
      // Only load users if organizationId is already set
      if (organizationId) {
        loadUsers();
      }
    }
  }, [isOpen, organizationId]);

  useEffect(() => {
    // Filter users based on selected user types
    if (selectedUserTypes.length > 0) {
      const filteredUsers = allUsers.filter(user => 
        !selectedUserTypes.includes(user.user_type)
      );
      setUsers(filteredUsers);
      
      // Remove any selected users that are now filtered out
      setSelectedUsers(prev => 
        prev.filter(userId => 
          filteredUsers.some(user => user.id === userId)
        )
      );
    } else {
      setUsers(allUsers);
    }
  }, [selectedUserTypes, allUsers]);

  useEffect(() => {
    // Load existing access rules when page changes
    if (page?.restricted_access_options) {
      const accessOptions = page.restricted_access_options;
      
      // Handle new nested organization-specific format
      let userTypes = [];
      let userList = [];
      
      if (organizationId && typeof accessOptions === 'object') {
        // Check for organization-specific rules first
        const orgRules = accessOptions[organizationId];
        if (orgRules) {
          userTypes = orgRules.user_types || [];
          userList = orgRules.user_list || [];
        } else {
          // Fall back to default rules if no org-specific rules
          const defaultRules = accessOptions.default || accessOptions;
          userTypes = defaultRules.user_types || [];
          userList = defaultRules.user_list || [];
        }
      } else {
        // Legacy format
        userTypes = accessOptions.user_types || [];
        userList = accessOptions.user_list || [];
      }
      
      setSelectedUserTypes(userTypes);
      setSelectedUsers(userList);
    } else {
      setSelectedUserTypes([]);
      setSelectedUsers([]);
    }
  }, [page, organizationId]);

  const loadUserTypes = () => {
    // Define user types (hardcoded as they match the system's user types)
    const systemUserTypes = [
      { id: 'client_admin', name: 'Client Admin' },
      { id: 'client_user', name: 'Client User' },
      { id: 'super_user', name: 'Super User' }
    ];
    
    setUserTypes(systemUserTypes);
  };

  const loadUsers = async () => {
    if (!organizationId) {
      console.log('No organization ID provided, skipping user load');
      return;
    }
    
    setLoading(true);
    try {
      // Use the correct API endpoint for organization users
      console.log('Loading users for organization:', organizationId);
      const usersResponse = await apiClient.get(`/organizations/${organizationId}/users`);
      
      console.log('Organization users response:', usersResponse);
      
      // Normalize the response - could be array or object with users property
      const users = Array.isArray(usersResponse) ? usersResponse : 
                   (usersResponse?.users || usersResponse?.data || []);
      
      // Normalize user data structure - handle nested detail object
      const normalizedUsers = users.map(user => {
        const detail = user.detail || {};
        return {
          id: user.id,
          email: detail.email || user.email,
          username: detail.user_name || user.username,
          first_name: detail.first_name || user.first_name,
          last_name: detail.last_name || user.last_name,
          full_name: detail.user_full_name || user.full_name,
          user_type: detail.user_type || user.user_type,
          organization_id: user.organization_id || organizationId,
          status: user.status
        };
      });
      
      console.log('Normalized users:', normalizedUsers);
      
      setAllUsers(normalizedUsers);
      setUsers(normalizedUsers);
      
    } catch (err) {
      console.error('Error loading users:', err);
      // Fallback to empty arrays
      setAllUsers([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };


  const handleSave = async () => {
    setSaving(true);
    try {
      const accessRules = {
        user_types: selectedUserTypes,
        user_list: selectedUsers
      };

      console.log('Saving access rules:', accessRules);
      
      // Call the parent save function
      await onSave(page.id, accessRules);
      
      onClose();
    } catch (err) {
      console.error('Error saving access rules:', err);
      alert('Failed to save access rules. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClearAll = () => {
    setSelectedUserTypes([]);
    setSelectedUsers([]);
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="modal-backdrop show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040
        }}
        onClick={onClose}
      />
      
      <div 
        className="modal show d-block" 
        style={{ zIndex: 1050 }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="ti-shield me-2"></i>
                Set Access Rules - {page?.page_title}
              </h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
                disabled={saving}
              />
            </div>
            
            <div className="modal-body">
              {loading ? (
                <div className="text-center p-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading access data...</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="alert alert-info">
                    <i className="ti-info-alt me-2"></i>
                    <strong>Access Rules Builder:</strong> Configure who can access this page. 
                    When user types are selected, individual users of those types are automatically excluded from the user selection.
                  </div>

                  <div className="row">
                    {/* Allow Access To Section */}
                    <div className="col-12">
                      <h6 className="mb-3">
                        <i className="ti-unlock me-2"></i>
                        Allow Access To
                      </h6>
                    </div>

                    {/* User Types Selection */}
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <h6 className="card-title">
                            User Types 
                            <span className="badge bg-primary ms-2">{selectedUserTypes.length}</span>
                          </h6>
                          <div className="form-text mb-3">
                            Select user types to grant access to all users of that type
                          </div>
                          
                          <select
                            className="form-select"
                            multiple
                            size="6"
                            value={selectedUserTypes}
                            onChange={(e) => {
                              const selected = Array.from(e.target.selectedOptions, option => option.value);
                              setSelectedUserTypes(selected);
                            }}
                            disabled={saving}
                            style={{ minHeight: '200px' }}
                          >
                            {userTypes.map(userType => (
                              <option 
                                key={userType.id} 
                                value={userType.id}
                              >
                                {userType.name} ({allUsers.filter(u => u.user_type === userType.id).length} users)
                              </option>
                            ))}
                          </select>
                          
                          <div className="form-text mt-2">
                            <i className="ti-info-alt me-1"></i>
                            Hold Ctrl/Cmd to select multiple user types
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Individual Users Selection */}
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <h6 className="card-title">
                            Individual Users 
                            <span className="badge bg-success ms-2">{selectedUsers.length}</span>
                          </h6>
                          <div className="form-text mb-3">
                            Select specific users to grant access
                            {selectedUserTypes.length > 0 && (
                              <div className="small text-warning mt-1">
                                <i className="ti-alert me-1"></i>
                                Users with selected types are hidden
                              </div>
                            )}
                          </div>
                          
                          {!organizationId ? (
                            <div className="text-muted text-center py-3">
                              <i className="ti-users display-4 d-block mb-2"></i>
                              Select an organization to load users
                            </div>
                          ) : users.length === 0 ? (
                            <div className="text-muted text-center py-3">
                              <i className="ti-users display-4 d-block mb-2"></i>
                              {loading ? 'Loading users...' : 
                               selectedUserTypes.length > 0 ? 
                                'All users are covered by selected user types' : 
                                'No users found for this organization'
                              }
                            </div>
                          ) : (
                            <>
                              <select
                                className="form-select"
                                multiple
                                size="6"
                                value={selectedUsers}
                                onChange={(e) => {
                                  const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                                  setSelectedUsers(selected);
                                }}
                                disabled={saving}
                                style={{ minHeight: '200px' }}
                              >
                                {users.map(user => {
                                  const displayName = user.full_name || 
                                    (user.first_name && user.last_name ? 
                                      `${user.first_name} ${user.last_name}` : 
                                      user.username || user.email || `User ${user.id}`);
                                  const userTypeLabel = user.user_type ? 
                                    ` [${user.user_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}]` : '';
                                  
                                  return (
                                    <option 
                                      key={user.id} 
                                      value={user.id}
                                    >
                                      {displayName}{userTypeLabel} - {user.email || 'No email'}
                                    </option>
                                  );
                                })}
                              </select>
                              
                              <div className="form-text mt-2">
                                <i className="ti-info-alt me-1"></i>
                                Hold Ctrl/Cmd to select multiple users
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Summary Section */}
                  {(selectedUserTypes.length > 0 || selectedUsers.length > 0) && (
                    <div className="card mt-3">
                      <div className="card-body">
                        <h6 className="card-title">Access Summary</h6>
                        <div className="row">
                          {selectedUserTypes.length > 0 && (
                            <div className="col-md-6">
                              <strong>User Types with Access:</strong>
                              <div className="mt-2">
                                {selectedUserTypes.map(type => (
                                  <span key={type} className="badge bg-primary me-1 mb-1">
                                    {type}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {selectedUsers.length > 0 && (
                            <div className="col-md-6">
                              <strong>Individual Users with Access:</strong>
                              <div className="mt-2">
                                {selectedUsers.map(userId => {
                                  const user = allUsers.find(u => u.id === userId);
                                  const displayName = user ? 
                                    (user.full_name || 
                                     (user.first_name && user.last_name ? 
                                       `${user.first_name} ${user.last_name}` : 
                                       user.username || user.email || `User ${userId}`)
                                    ) : `User ${userId}`;
                                  return (
                                    <span key={userId} className="badge bg-success me-1 mb-1">
                                      {displayName}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-outline-secondary me-2" 
                onClick={handleClearAll}
                disabled={saving || loading}
              >
                <i className="ti-trash me-1"></i>
                Clear All
              </button>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || loading}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ti-save me-2"></i>
                    Save Access Rules
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

    </>
  );
};

export default AccessRulesModal;